import React, { useEffect, useMemo, useState } from 'react';
import { useRefState } from '@libs/shared';
import { getVerifyProfile } from '../../../apis';
import { VerifyProfile } from '../../../interfaces';
import VerificationGuardPanel from '../components/verification-guard-panel';
import type { AuthTarget, GuardOptions } from '../types';
import { getDerivedStatus, openVerificationAlert, openVerifyPage } from '../utils';
import { runEntryClick } from '../utils/openVerificationAlert';

const DEFAULT_POLLING_INTERVAL = 5000;

/**
 * 管理租户认证准入状态，统一提供弹窗阻断和内容区阻断能力。
 */
const useVerificationGuard = ({
    helpUrl,
    panelSize = 'medium'
}: {
    helpUrl?: string;
    panelSize?: 'small' | 'medium' | 'large';
} = {}) => {
    const [profile, setProfile, profileRef] = useRefState<VerifyProfile | undefined>(undefined);
    const [loading, setLoading] = useState(true);

    const requestProfile = (showLoading = true) => {
        if (showLoading) {
            setLoading(true);
        }

        return getVerifyProfile()
            .then((nextProfile) => {
                setProfile(nextProfile);
                return nextProfile;
            })
            .finally(() => {
                if (showLoading) {
                    setLoading(false);
                }
            });
    };

    useEffect(() => {
        requestProfile();
    }, []);

    const currentStatus = useMemo(() => getDerivedStatus(profile), [profile]);

    const handleVerify = (
        target: AuthTarget = currentStatus.nextAuthTarget,
        options: GuardOptions = {}
    ) => {
        runEntryClick(options.onEntryClick, target, () => {
            openVerifyPage(target);
        });
    };

    const renderGuard = (children: React.ReactNode, options: GuardOptions = {}) => {
        if (currentStatus.verified) {
            return children;
        }

        return (
            <VerificationGuardPanel
                size={panelSize}
                helpUrl={helpUrl}
                loading={loading}
                pollingInterval={DEFAULT_POLLING_INTERVAL}
                status={currentStatus}
                onVerify={(target) => handleVerify(target, options)}
                onRefresh={() => requestProfile(false)}
            />
        );
    };

    const guard = (action: () => unknown, options: GuardOptions = {}) => {
        const handleGuardStatus = (nextProfile?: VerifyProfile) => {
            const latestProfile = nextProfile || profileRef.current;
            const latestStatus = getDerivedStatus(latestProfile);

            if (latestStatus.verified) {
                action();
                return;
            }

            openVerificationAlert(latestProfile, {
                helpUrl,
                onEntryClick: options.onEntryClick
            });
        };

        requestProfile(false).then(
            (nextProfile) => {
                handleGuardStatus(nextProfile);
            },
            () => {
                handleGuardStatus(profileRef.current);
            }
        );
    };

    return {
        loading,
        guard,
        renderGuard
    };
};

export default useVerificationGuard;
